import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { Box, Button, Divider } from "@mui/material";
import {
  Place,
  Memory,
  Receipt,
  Speed,
  AttachMoney,
  People,
  Event,
  GetApp,
  PriceChange,
  PriceCheck,
  CalendarMonth,
  Waves,
  Public,
} from "@mui/icons-material";
import { isEqual } from "lodash";
import { mapValues, mapValuesToSaveData, SpectrumPricingValuesType } from "../../utils";
import allRoutes from "../../constants/routes";
import {
  setValues as setSpectrumPricingValues,
  selectSpectrumPricingSlice,
  setSavedValues,
  setCurrentView,
} from "../../store/slices/spectrumPricing";
import { setSpectrumPricingFilters, selectUiState, setFiltersOpen, closeSpectrumPricingFilters } from "../../store/slices/ui";
import {
  useGetCountriesQuery,
  useGetSpectrumFrequencyBandsQuery,
  useGetTechnologiesQuery,
  useGetLicencesQuery,
  useGetFilterRangesQuery,
  useGetFrequencyBandsAllQuery,
  useGetItuRegionsQuery,
} from "../../store/apis/search";
import { useSaveSearchMutation } from "../../store/apis/save";
import FilterList from "../FilterList";
import SearchFilter from "../SearchFilter";
import SliderFilter from "../SliderFilter";
import FrequenciesFilter from "../FrequenciesFilter";
import { getYear } from "date-fns";
import colors from "../../theme/colors";

const initialValues: SpectrumPricingValuesType = {
  countries: [],
  bands: [],
  operators: [],
  technologies: [],
  licences: [],
  allocated_bandwidth: [],
  allocated_bandwidth_control: "and",
  price_total_min: 0,
  price_total_max: 10881,
  price_population_min: 0,
  price_population_max: 3460,
  completed_year_min: 2010,
  completed_year_max: new Date().getFullYear(),
  ituRegions: [],
};

const SpectrumPricingForm: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { values: valuesCurrent, lastSaved, currentView } = useSelector(selectSpectrumPricingSlice);
  const { spectrumPricing: filters } = useSelector(selectUiState);

  const { values, setFieldValue, setValues, handleSubmit, handleReset, resetForm } = useFormik({
    initialValues,
    onSubmit: values => {
      dispatch(setFiltersOpen(false));
      dispatch(setSpectrumPricingValues(values));

      const { path } = allRoutes.spectrum_pricing;

      // Navigate to the current view instead of always going to "search"
      const targetPath = currentView === "bubble" ? `${path}/search/bubble` : `${path}/search`;

      if (pathname !== targetPath) {
        navigate(targetPath);
      }
    },
  });

  const { data: ituRegionsOptions = [], isLoading: isItuRegionsLoading } = useGetItuRegionsQuery();
  const { data: countriesOptions = [], isLoading: isCountriesLoading } = useGetCountriesQuery({
    ituRegions: values.ituRegions,
    regions: [],
  });
  const { data: frequenciesOptions = [], isLoading: isFrequenciesLoading } = useGetSpectrumFrequencyBandsQuery();
  const { data: technologiesOptions = [], isLoading: isTechnologiesLoading } = useGetTechnologiesQuery(6);
  const { data: licencesOptions = [], isLoading: isLicencesLoading } = useGetLicencesQuery();
  const { data: filterRanges } = useGetFilterRangesQuery();
  const { data: frequencyBandsOptions = [[], [], []], isLoading: isFrequencyBandsLoading } = useGetFrequencyBandsAllQuery({
    technologies: values.technologies,
    sectionId: 2,
  });
  const [options1, options2, options3] = frequencyBandsOptions;

  useEffect(() => {
    const frequencyBandsFiltered = values.allocated_bandwidth.filter((option: number) => {
      return mapValues(options1).includes(option) || mapValues(options2).includes(option) || mapValues(options3).includes(option);
    });

    if (!isEqual(frequencyBandsFiltered, values.allocated_bandwidth)) {
      setFieldValue("frequencyBands", frequencyBandsFiltered);
    }
  }, [options1, options2, options3, values.allocated_bandwidth, setFieldValue]);

  useLayoutEffect(() => {
    setValues(valuesCurrent);
  }, [setValues, valuesCurrent]);

  useEffect(() => {
    const countriesFiltered = values.countries.filter(option => mapValues(countriesOptions).includes(option));

    if (!isEqual(countriesFiltered, values.countries)) {
      setFieldValue("countries", countriesFiltered);
    }
  }, [countriesOptions, values.countries, setFieldValue]);

  const setFilterOpen = useCallback(
    (key: string, value: boolean) => {
      dispatch(setFiltersOpen(value));
      dispatch(setSpectrumPricingFilters({ ...filters, [key]: value }));
    },
    [dispatch, filters],
  );

  const [saveSearch] = useSaveSearchMutation();

  const handleSave = (title: string) => {
    dispatch(setSavedValues(valuesCurrent));
    saveSearch({
      sectionId: 7,
      data: { title, ...mapValuesToSaveData(7, valuesCurrent) },
    });
  };

  const price_max = filterRanges?.price_total?.max_milion_rounded ?? values.price_total_max;
  const price_min = filterRanges?.price_total?.min_milion ?? values.price_total_min;
  const price_population_max = filterRanges
    ? Math.floor(filterRanges.price_population?.max_rounded ?? values.price_population_max)
    : values.price_population_max;
  const price_population_min = filterRanges
    ? Math.floor(filterRanges.price_population?.min ?? values.price_population_min)
    : values.price_population_min;
  const completed_year_max = filterRanges?.completed_year?.max ?? values.completed_year_max;
  const completed_year_min = filterRanges?.completed_year?.min ?? values.completed_year_min;

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const valuesCurrentRef = useRef(valuesCurrent);
  valuesCurrentRef.current = valuesCurrent;

  const filterRangesMergedRef = useRef(false);

  useLayoutEffect(() => {
    if (!filterRanges) {
      filterRangesMergedRef.current = false;
      return;
    }

    const base = filterRangesMergedRef.current ? valuesRef.current : valuesCurrentRef.current;
    resetForm({
      values: {
        ...base,
        price_total_max: price_max,
        price_total_min: price_min,
        price_population_max,
        price_population_min,
        completed_year_max,
        completed_year_min,
      },
    });
    filterRangesMergedRef.current = true;
  }, [filterRanges, price_max, price_min, price_population_max, price_population_min, completed_year_max, completed_year_min, resetForm]);

  return (
    <FilterList
      disabled={isCountriesLoading || isTechnologiesLoading || isLicencesLoading}
      canSearch={!isEqual(values, valuesCurrent)}
      canSave={!isEqual(valuesCurrent, lastSaved)}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onSave={handleSave}
    >
      <Box paddingX={8} paddingY={4} textAlign="right">
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            dispatch(closeSpectrumPricingFilters());
          }}
          style={{ backgroundColor: colors.white }}
        >
          Close all
        </Button>
      </Box>
      <Divider />
      <SearchFilter
        title="ITU Region"
        name="itu-regions-filter"
        tooltip="Filter the search by ITU Region"
        value={ituRegionsOptions.filter(option => values.ituRegions.includes(option.id))}
        icon={<Public fontSize="small" />}
        options={ituRegionsOptions}
        loading={isItuRegionsLoading}
        disabled={!ituRegionsOptions.length}
        open={filters.ituRegions}
        onChange={value => {
          setFieldValue("ituRegions", mapValues(value));
          setFieldValue("countries", []);
        }}
        setOpen={value => setFilterOpen("ituRegions", value)}
      />
      <SearchFilter
        title="Country/Territory"
        name="countries-filter"
        tooltip="Filter by specific country"
        value={countriesOptions.filter(option => values.countries.includes(option.id))}
        icon={<Place fontSize="small" />}
        options={countriesOptions}
        loading={isCountriesLoading}
        disabled={!countriesOptions.length}
        open={filters.countries}
        onChange={value => setFieldValue("countries", mapValues(value))}
        setOpen={value => setFilterOpen("countries", value)}
      />
      <SearchFilter
        title="Licence"
        name="licences-filter"
        tooltip="Filter by specific licence"
        value={licencesOptions.filter((option: any) => values.licences.includes(option.id))}
        icon={<Receipt fontSize="small" />}
        options={licencesOptions}
        loading={isLicencesLoading}
        disabled={!licencesOptions.length}
        open={filters.licences}
        onChange={value => setFieldValue("licences", mapValues(value))}
        setOpen={value => setFilterOpen("licences", value)}
      />
      <FrequenciesFilter
        title="Frequency"
        name="frequency-bands-filter"
        tooltip="Filter the search by Frequencies"
        value={[
          options1.filter(option => values.allocated_bandwidth.includes(option.id)),
          options2.filter(option => values.allocated_bandwidth.includes(option.id)),
          options3.filter(option => values.allocated_bandwidth.includes(option.id)),
        ]}
        icon={<Waves fontSize="small" />}
        options={frequencyBandsOptions}
        loading={isFrequencyBandsLoading}
        disabled={!frequencyBandsOptions.length}
        open={filters.allocated_bandwidth}
        setOr={value => setFieldValue("allocated_bandwidth_control", value)}
        orValue={values.allocated_bandwidth_control}
        onChange={value => setFieldValue("allocated_bandwidth", mapValues(value.flat()))}
        setOpen={value => setFilterOpen("allocated_bandwidth", value)}
      />
      <SearchFilter
        title="Technology"
        name="technologies-filter"
        tooltip="Filter by specific technology"
        value={technologiesOptions.filter(option => values.technologies.includes(option.id))}
        icon={<Memory fontSize="small" />}
        options={technologiesOptions}
        loading={isTechnologiesLoading}
        disabled={!technologiesOptions.length}
        open={filters.technologies}
        onChange={value => setFieldValue("technologies", mapValues(value))}
        setOpen={value => setFilterOpen("technologies", value)}
      />
      <SliderFilter
        title="Total Price"
        name="price_total-filter"
        tooltip="Filter by total price"
        icon={<PriceCheck fontSize="small" />}
        value={[values.price_total_min, values.price_total_max]}
        open={filters.price_total}
        setOpen={value => setFilterOpen("price_total", value)}
        onChange={value => {
          setFieldValue("price_total_min", value[0]);
          setFieldValue("price_total_max", value[1]);
        }}
        min={price_min}
        max={price_max}
        note="in millions $"
        marks={[
          { value: price_min, label: String(price_min) },
          { value: price_max, label: String(price_max) },
        ]}
      />
      <SliderFilter
        title="$/MHz/1000 pop."
        name="price_population-filter"
        tooltip="Filter by population price"
        icon={<PriceCheck fontSize="small" />}
        value={[values.price_population_min, values.price_population_max]}
        open={filters.price_population}
        setOpen={value => setFilterOpen("price_population", value)}
        onChange={value => {
          setFieldValue("price_population_min", value[0]);
          setFieldValue("price_population_max", value[1]);
        }}
        min={price_population_min}
        max={price_population_max}
        marks={[
          { value: price_population_min, label: String(price_population_min) },
          { value: price_population_max, label: String(price_population_max) },
        ]}
      />
      <SliderFilter
        title="Completed Year"
        name="year-range-filter"
        tooltip="Search by year range"
        icon={<CalendarMonth fontSize="small" />}
        value={[values.completed_year_min, values.completed_year_max]}
        open={filters.completed_year}
        setOpen={value => setFilterOpen("completed_year", value)}
        onChange={value => {
          setFieldValue("completed_year_min", value[0]);
          setFieldValue("completed_year_max", value[1]);
        }}
        min={completed_year_min}
        max={completed_year_max}
        marks={[
          { value: completed_year_min, label: String(completed_year_min) },
          { value: completed_year_max, label: String(completed_year_max) },
        ]}
      />
    </FilterList>
  );
};

export default SpectrumPricingForm;
